'use client';

import { useState } from 'react';
import { useLanguage } from '@/components/language-provider';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChevronDown, Download } from 'lucide-react';

interface FAQItem {
  id: string;
  questionKey: string;
  answerKey: string;
  downloadLabelKey?: string;
}

const faqItems: FAQItem[] = [
  {
    id: 'founder',
    questionKey: 'faq.founder.question',
    answerKey: 'faq.founder.answer',
  },
  {
    id: 'team',
    questionKey: 'faq.team.question',
    answerKey: 'faq.team.answer',
  },
  {
    id: 'positioning',
    questionKey: 'faq.positioning.question',
    answerKey: 'faq.positioning.answer',
  },
  {
    id: 'features',
    questionKey: 'faq.features.question',
    answerKey: 'faq.features.answer',
  },
  {
    id: 'process',
    questionKey: 'faq.process.question',
    answerKey: 'faq.process.answer',
  },
  {
    id: 'mold',
    questionKey: 'faq.mold.question',
    answerKey: 'faq.mold.answer',
    downloadLabelKey: 'faq.mold.downloadLabel',
  },
  {
    id: 'overseas',
    questionKey: 'faq.overseas.question',
    answerKey: 'faq.overseas.answer',
  },
];

export function FAQSection() {
  const { t, language } = useLanguage();
  const [openId, setOpenId] = useState<string | null>(null);

  const toggleFAQ = (id: string) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('faq.title')}</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('faq.subtitle')}
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-4">
          {faqItems.map((item) => (
            <Card key={item.id} className="overflow-hidden">
              <CardHeader
                className="cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => toggleFAQ(item.id)}
              >
                <CardTitle className="flex items-center justify-between text-lg font-medium pr-4">
                  <span>{t(item.questionKey)}</span>
                  <ChevronDown
                    className={`h-5 w-5 text-muted-foreground transition-transform duration-200 ${
                      openId === item.id ? 'rotate-180' : ''
                    }`}
                  />
                </CardTitle>
              </CardHeader>
              {openId === item.id && (
                <CardContent className="pt-0">
                  <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                    {t(item.answerKey)}
                  </p>
                  {item.downloadLabelKey && (
                    <a
                      href={`/downloads/requirements-form-${language}.pdf`}
                      download
                      className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
                    >
                      <Download className="h-4 w-4" />
                      {t(item.downloadLabelKey)}
                    </a>
                  )}
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FAQPage() {
  const { t, language } = useLanguage();
  const [openId, setOpenId] = useState<string | null>(null);

  const toggleFAQ = (id: string) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-primary-foreground py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">{t('faq.title')}</h1>
          <p className="text-lg opacity-90">{t('faq.subtitle')}</p>
        </div>
      </header>

      {/* FAQ Content */}
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto space-y-4">
          {faqItems.map((item) => (
            <Card key={item.id} className="overflow-hidden">
              <CardHeader
                className="cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => toggleFAQ(item.id)}
              >
                <CardTitle className="flex items-center justify-between text-lg font-medium pr-4">
                  <span>{t(item.questionKey)}</span>
                  <ChevronDown
                    className={`h-5 w-5 text-muted-foreground transition-transform duration-200 ${
                      openId === item.id ? 'rotate-180' : ''
                    }`}
                  />
                </CardTitle>
              </CardHeader>
              {openId === item.id && (
                <CardContent className="pt-0">
                  <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                    {t(item.answerKey)}
                  </p>
                  {item.downloadLabelKey && (
                    <a
                      href={`/downloads/requirements-form-${language}.pdf`}
                      download
                      className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
                    >
                      <Download className="h-4 w-4" />
                      {t(item.downloadLabelKey)}
                    </a>
                  )}
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
}
